import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { contactMessagesApi } from '@/services/contactMessages.service';
import type {
  ContactMessage,
  ContactMessageListResult,
  ContactMessageStatus,
} from '@/types/contactMessage.types';

const PAGE_SIZE = 20;
const CONTACT_MESSAGES_QUERY_KEY = 'contact-messages';

export type StatusFilter = 'all' | ContactMessageStatus;

interface UseContactMessagesPageResult {
  messages: ContactMessage[];
  total: number;
  page: number;
  totalPages: number;
  statusFilter: StatusFilter;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  mutatingId: string | null;
  onChangeStatus: (status: StatusFilter) => void;
  onChangePage: (page: number) => void;
  onMarkRead: (message: ContactMessage) => void;
  onArchive: (message: ContactMessage) => void;
}

export const useContactMessagesPage = (): UseContactMessagesPageResult => {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [mutatingId, setMutatingId] = useState<string | null>(null);

  const listQuery = useQuery<ContactMessageListResult>({
    queryKey: [CONTACT_MESSAGES_QUERY_KEY, { status: statusFilter, page }],
    queryFn: () =>
      contactMessagesApi.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: (previous) => previous,
  });

  const statusMutation = useMutation<
    ContactMessage,
    unknown,
    { id: string; status: ContactMessageStatus }
  >({
    mutationFn: ({ id, status }) => contactMessagesApi.updateStatus(id, status),
    onMutate: ({ id }) => setMutatingId(id),
    onSettled: () => {
      setMutatingId(null);
      void queryClient.invalidateQueries({ queryKey: [CONTACT_MESSAGES_QUERY_KEY] });
    },
  });

  return {
    messages: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    page: listQuery.data?.page ?? page,
    totalPages: listQuery.data?.totalPages ?? 1,
    statusFilter,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    errorMessage: listQuery.error instanceof Error ? listQuery.error.message : null,
    mutatingId,
    onChangeStatus: (status) => {
      setStatusFilter(status);
      setPage(1);
    },
    onChangePage: setPage,
    onMarkRead: (message) => statusMutation.mutate({ id: message.id, status: 'read' }),
    onArchive: (message) => statusMutation.mutate({ id: message.id, status: 'archived' }),
  };
};
